import React, { useEffect, useState } from "react";
import mixpanel from "mixpanel-browser";
import axios from "axios";
import logo from "../sidebar-logo.png";
import { observer } from "mobx-react";
import ShareModal from '../Components/ShareModal/index';
import { Share as ShareIcon } from '@mui/icons-material';
import { Link, useHistory, useLocation } from "react-router-dom";
import { Helmet } from 'react-helmet';
import "./Share.scss";
import "./FeedLanding.scss";
import { CircularProgress } from "@mui/material";
import { getLatestFeedArticleFromFeed, getAllFeeds, getFeed } from "../audiblogsApi";
import OneGiftLogo from '../Components/OneGiftLogo';
import discoverStore from "../Pages/DiscoverNew/store";
import FeedSubscribed from "../Pages/DiscoverNew/Components/FeedSubscribed";
import { RssFeedRoundedIcon } from '@mui/icons-material';
import { setLandingPage } from '../utils/trackingParams';

const FeedLanding = observer(
  ({
    match: {
      params: { feedId },
    },
  }) => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const currentParams = searchParams.toString();
    // Keep feedId clean without query params
    const cleanFeedId = decodeURIComponent(feedId).split('?')[0];
    const fullFeedUrl = `${cleanFeedId}${window.location.pathname.slice(feedId.length + 6)}`;
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [feed, setFeed] = useState(null);
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const [expandedArticle, setExpandedArticle] = useState(null);
    const history = useHistory();
    const [allFeeds, setAllFeeds] = useState([]);
    const [displayedFeeds, setDisplayedFeeds] = useState([]);
    const [feedsLoading, setFeedsLoading] = useState(true);

    // Add effect to preserve URL parameters
    useEffect(() => {
      // If we have params but they're not in the URL, update the URL
      if (currentParams && !location.search) {
        history.replace({
          pathname: location.pathname,
          search: currentParams
        });
      }
    }, [location, history, currentParams]);

    // Generate structured data for SEO
    const generateStructuredData = () => {
      if (!feed) return null;

      return {
        "@context": "https://schema.org",
        "@type": "PodcastSeries",
        "name": feed.title,
        "description": feed.description,
        "url": `${window.location.origin}/feed/${feedId}`,
        "image": feed.thumbnail_url
      };
    };

    useEffect(() => {
      // Track page visit with consistent event name
      mixpanel.track('Landing Page Visited', {
        page: 'feed_landing',
        feed_id: fullFeedUrl,
        utm_landingpage: 'feed_landing'
      });
      setLandingPage('feed_landing');
      mixpanel.start_session_recording();
      console.log("fullFeedUrl - getting feed", fullFeedUrl);
      
      getFeed(fullFeedUrl)
        .then(({ feed: feedData, articles: articlesWithText }) => {
          setFeed(feedData);
          setArticles(articlesWithText.filter(article => !article.withads));
          console.log("Articles set:", articlesWithText);
          setLoading(false);
        })
        .catch(error => {
          console.error("Error fetching feed:", error);
          setLoading(false);
        });
    }, [fullFeedUrl]);

    // Add new useEffect for fetching all feeds
    useEffect(() => {
      getAllFeeds()
        .then(async (response) => {
          if (response && response.data && response.data.feeds) {
            const processedFeeds = response.data.feeds.map(feed => ({
              ...feed,
              feed_id: feed.feed_id || feed.id || `feed-${Math.random()}`,
              title: feed.title || feed.name || "Untitled Feed",
              date_added: feed.date_added || feed.created_at || new Date().toISOString(),
              article_id: feed.last_article_url || feed.feed_id || feed.id,
              audio_url: feed.audio_url || "https://audioread.com/share/placeholder.mp3",
            }));
            
            const feedsWithThumbnails = await Promise.all(
              processedFeeds.map(async (feed) => {
                try {
                  if (feed.feed_id) {
                    const latestArticle = await getLatestFeedArticleFromFeed(feed.feed_id);
                    if (latestArticle) {
                      return {
                        ...feed,
                        thumbnail_url: latestArticle.thumbnail_url || feed.thumbnail_url,
                        audio_url: latestArticle.audio_url,
                        article_id: latestArticle.article_id,
                        article_title: latestArticle.title,
                        article_url: latestArticle.url,
                        latestArticle: latestArticle
                      };
                    }
                  }
                  return feed;
                } catch (error) {
                  console.error(`Error fetching latest article for feed ${feed.feed_id}:`, error);
                  return feed;
                }
              })
            );
            
            setAllFeeds(feedsWithThumbnails);
            setDisplayedFeeds(feedsWithThumbnails.slice(0, 9));
          }
          setFeedsLoading(false);
        })
        .catch(error => {
          console.error("Error fetching feeds:", error);
          setFeedsLoading(false);
        });
    }, []);

    // Redirect to signin when trying to subscribe
    const handleSubscribe = () => {
      history.push('/signin?source=feed_landing_subscribe');
    };

    if (loading) {
      return (
        <div className="center">
          <CircularProgress />
        </div>
      );
    }

    return (
      <>
        <Helmet>
          <title>{feed?.title ? `${feed.title} - Listen to the podcast version | Audioread` : 'Audioread Feeds'}</title>
          <meta name="description" content={feed?.description || 'Listen to this podcast feed on Audioread. Convert any content into audio and listen in your favorite podcast app.'} />
          <meta name="keywords" content={`${feed?.title || ''}, podcast feed, audio articles, ${feed?.author || ''}, Audioread`} />
          
          <meta property="og:title" content={feed?.title ? `${feed.title} - Podcast Feed` : 'Audioread Feed'} />
          <meta property="og:description" content={feed?.description || 'Listen to this podcast feed on Audioread'} />
          <meta property="og:image" content={feed?.thumbnail_url || 'https://audioread.com/logo.png'} />
          <meta property="og:url" content={`${window.location.origin}/feed/${feedId}`} />
          <meta property="og:type" content="website" />
          
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={feed?.title ? `${feed.title} - Podcast Feed` : 'Audioread Feed'} />
          <meta name="twitter:description" content={feed?.description || 'Listen to this podcast feed on Audioread'} />
          <meta name="twitter:image" content={feed?.thumbnail_url || 'https://audioread.com/logo.png'} />
          
          {feed && (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "PodcastSeries",
                "name": feed.title,
                "description": feed.description,
                "url": `${window.location.origin}/feed/${feedId}`,
                "image": feed.thumbnail_url,
                "episode": articles.slice(0, 5).map(article => ({
                  "@type": "PodcastEpisode",
                  "name": article.title,
                  "description": article.text?.substring(0, 200) || "",
                  "datePublished": article.date_added,
                  "image": article.thumbnail_url,
                  "url": article.url
                }))
              })}
            </script>
          )}
        </Helmet>

        <div className="feed-landing-page">
          {/* Mobile Header */}
          <div className="mobile-header">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <img src={logo} className="audiblogs-logo" alt="logo" />
            </Link>
            <Link 
              className="mobile-cta-button" 
              to={`/signin?source=feed_landing&utm_source=feed_landing${currentParams ? `&${currentParams}` : ''}`}
            >
              Start Listening
            </Link>
          </div>

          <div className="nav-bar">
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <img src={logo} className="audiblogs-logo" alt="logo" />
              </Link>
              <div style={{ marginLeft: '50px' }} className="ph-badge">
                <a
                  href="https://www.producthunt.com/posts/audiblogs?utm_source=badge-top-post-badge&utm_medium=badge&utm_souce=badge-audiblogs"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://api.producthunt.com/widgets/embed-image/v1/top-post-badge.svg?post_id=283759&theme=dark&period=monthly"
                    alt="Audioread - Listen to any web article in your podcast player | Product Hunt"
                    style={{
                      width: '250px',
                      height: '54px',
                    }}
                    width="250"
                    height="54"
                  />
                </a>
              </div>
            </div>
            <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '20px', fontSize: '1.05rem', fontWeight: 'bold' }}>
                <Link
                  className="signin-button"
                  to={{ pathname: "https://help.audioread.com/en/collections/12375780-use-cases" }}
                  target="_blank"
                >
                  Use Cases
                </Link>
                <Link
                  className="signin-button"
                  to="/feeds"
                >
                  Feeds
                </Link>
                <Link
                  className="signin-button"
                  to={`/signin?source=feed_nav_signin${currentParams ? `&${currentParams}` : ''}`}
                >
                  Sign In
                </Link>
                <div style={{display: 'flex', alignItems: 'center', transform: 'scale(0.75)', marginLeft: '-20px'}}>
                  <div className="cta-div" style={{ position: 'static', width: 'auto', paddingBottom: '18px' }}>
                    <Link
                      className="cta-button"
                      to={`/signin?source=feed_nav_cta${currentParams ? `&${currentParams}` : ''}`}
                      style={{ marginTop: '0px' }}
                    >
                      Start listening
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="share-page">
            <div className="share-content">
              {feed && (
                <div className="feed-info">
                  {feed.thumbnail_url && (
                    <div className="feed-thumbnail">
                      <img 
                        src={feed.thumbnail_url} 
                        alt={feed.title}
                      />
                    </div>
                  )}
                  <h1 style={{
                    fontSize: "2.5em",
                    margin: "20px 0",
                    color: "#fff",
                    textAlign: "center"
                  }}>
                    {feed.title || "Loading feed..."}
                  </h1>
                  <p>{feed.description}</p>
                  {feed.author && <p className="feed-author">By {feed.author}</p>}
                </div>
              )}
              
              <div className="share-actions">
                <button
                  className="share-button"
                  onClick={() => setIsShareModalOpen(true)}
                >
                  <ShareIcon /> Share
                </button>
              </div>

              <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                feed={feed}
              />

              {articles.length > 0 && (
                <div className="playlist-section">
                  <h2>Latest Episodes</h2>
                  <div className="playlist">
                    {articles.slice(0, 5).map((article) => (
                      <div 
                        key={article.uniqueId}
                        className={`playlist-item ${currentlyPlaying?.id === article.id ? 'active' : ''}`}
                        onClick={() => setCurrentlyPlaying(article)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="article-info" style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                          {article.thumbnail_url && (
                            <div className="article-thumbnail" style={{ flexShrink: 0 }}>
                              <img 
                                src={article.thumbnail_url} 
                                alt={`${article.title} thumbnail`}
                                style={{
                                  width: '120px',
                                  height: '120px',
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                              />
                            </div>
                          )}
                          <div className="article-content" style={{ flex: 1 }}>
                            <h3>{article.title}</h3>
                            <p>{new Date(article.date_added).toLocaleDateString()}</p>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                              <button
                                className="transcript-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedArticle(expandedArticle === article.uniqueId ? null : article.uniqueId);
                                }}
                                style={{
                                  padding: '4px 12px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  background: '#f0f0f0',
                                  color: '#333',
                                  cursor: 'pointer',
                                  fontSize: '14px'
                                }}
                              >
                                {expandedArticle === article.uniqueId ? 'Hide Transcript' : 'Show Transcript'}
                              </button>
                            </div>
                          </div>
                        </div>
                        {currentlyPlaying?.id === article.id && (
                          <div className="audio-player">
                            <audio 
                              controls 
                              controlsList="nodownload"
                              title="Subscribe to download audio files"
                              src={article.audio_url}
                              style={{ width: '100%', marginTop: '10px' }}
                              onEnded={() => setCurrentlyPlaying(null)}
                            >
                              Your browser does not support the audio element.
                            </audio>
                          </div>
                        )}
                        {expandedArticle === article.uniqueId && (
                          <div style={{
                            marginTop: '15px',
                            padding: '15px',
                            background: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '8px',
                            color: '#fff',
                            whiteSpace: 'pre-wrap',
                            textAlign: 'left',
                            lineHeight: '1.6',
                            fontSize: '14px'
                          }}>
                            {article.text ? article.text : "No transcript available"}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="subscribe-section">
                <h2>Subscribe now to add this feed and many more to your podcast player!</h2>
                <button 
                  className="subscribe-button" 
                  onClick={() => history.push(`/signin?source=feed_subscribe${currentParams ? `&${currentParams}` : ''}`)}
                >
                  Subscribe Now
                </button>
              </div>

              {/* All Feeds Section */}
              <div className="all-feeds-section">
                <div className="section-header">
                  <div className="divider-line"></div>
                  <h2>Discover More Feeds</h2>
                  <div className="divider-line"></div>
                </div>

                <div className="feeds-grid">
                  {feedsLoading ? (
                    <div className="feeds-loading-container">
                      <CircularProgress 
                        size={40} 
                        sx={{
                          color: '#4292fa',
                          '@keyframes gradient': {
                            '0%': { color: '#4292fa' },
                            '25%': { color: '#8535cd' },
                            '50%': { color: '#e82186' },
                            '75%': { color: '#ff5c57' },
                            '100%': { color: '#4292fa' }
                          },
                          animation: 'gradient 2s ease infinite'
                        }}
                      />
                      <p>Loading feeds...</p>
                    </div>
                  ) : (
                    <div className="feeds-grid-content">
                      {displayedFeeds.map((feed) => (
                        <Link 
                          key={feed.feed_id || feed.id || Math.random().toString()}
                          to="/signin?source=feed_landing_subscribe"
                          className="feed-link"
                        >
                          <FeedSubscribed
                            feed={feed}
                            idToken={null}
                            selectArticle={() => {}}
                            isActive={false}
                            onUnsubscribe={() => {}}
                            isSubscribed={false}
                          />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
);

export default FeedLanding; 